import type { ApolloCache } from "@apollo/client";

import { gql } from "@medbillai/generated-graphql-types";

import { getYourCasesQuery } from "../sections/queries";

export const checkLinksQuery = gql(/* GraphQL */ `
  query CheckInsuranceLinks {
    me {
      id
      flexpaLinks {
        id
        active
      }
      webAgentLinks {
        __typename
        id
        active
        endpoint {
          ... on webAgent__Endpoint_Type {
            type
          }
        }
      }
    }
  }
`);

export const getPersonalizedPromptsQuery = gql(/* GraphQL */ `
  query GetPersonalizedPrompts {
    getPersonalizedPrompts {
      prompts {
        id
        text
        response
        caseType
        icon
      }
    }
  }
`);

type ActionListQueryData = {
  me?: {
    issues?: Array<{
      id: string;
      actions?: Array<{ id: string; eventType?: string }> | null;
    }> | null;
  } | null;
};

export const removeActionFromCache = (
  cache: ApolloCache<unknown>,
  actionId: string,
) => {
  cache.updateQuery<ActionListQueryData>({ query: getYourCasesQuery }, prev => {
    const issues = prev?.me?.issues;
    const hasAction = issues?.some(issue =>
      issue.actions?.some(action => action.id === actionId),
    );
    if (!prev?.me || !issues || !hasAction) {
      // Returning null skips the write — the query isn't cached or the
      // action isn't in it.
      return null;
    }
    return {
      ...prev,
      me: {
        ...prev.me,
        issues: issues.map(issue => ({
          ...issue,
          actions: (issue.actions ?? []).filter(
            action => action.id !== actionId,
          ),
        })),
      },
    };
  });
};

// Owner reply clears staff CHAT actionRequired server-side, but Issue.actions
// still includes unseen staff CHAT (`not exists .seenAt`). Strip every CHAT
// action from the home cache so "New message" / "Respond to your case"
// disappears immediately — matching the post-reply server state once seenAt is
// stamped.
export const clearStaffChatActionsFromHomeCache = (
  cache: ApolloCache<unknown>,
  issueId: string,
) => {
  cache.modify({
    id: cache.identify({ __typename: "issue__Issue_Type", id: issueId }),
    fields: {
      actions(existing: readonly { __ref?: string }[] = [], { readField }) {
        return existing.filter(
          action => readField("eventType", action) !== "CHAT",
        );
      },
    },
  });

  cache.updateQuery<ActionListQueryData>({ query: getYourCasesQuery }, prev => {
    const issues = prev?.me?.issues;
    const issue = issues?.find(item => item.id === issueId);
    const hasChat = issue?.actions?.some(action => action.eventType === "CHAT");
    if (!prev?.me || !issues || !hasChat) {
      return null;
    }
    return {
      ...prev,
      me: {
        ...prev.me,
        issues: issues.map(item =>
          item.id === issueId
            ? {
                ...item,
                actions: (item.actions ?? []).filter(
                  action => action.eventType !== "CHAT",
                ),
              }
            : item,
        ),
      },
    };
  });
};
